import React, { useCallback, useEffect, useRef, useState } from "react";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import WalletTransferModals from "../WalletPage/WalletTransferModals";
import { useWalletTransfer } from "../WalletPage/useWalletTransfer";
import WalletOverviewSkeleton from "../../../customComponents/WalletOverviewSkeleton";
import SoMobileFilters from "../Order/SoMobileFilters";
import { BorrowRepayModal } from "../MarginAccountPage/index";
import CrossMarginLevelGauge from "../cross_margin/CrossMarginLevelGauge";
import "../FundingAccountPage/fundingAccount.css";
import "../AssetOverview/assetOverview.css";
import "../SpotOrders/spotOrders.css";
import { useNavigate } from "react-router-dom";

const POLL_INTERVAL_MS = 4000;
const DEBTS_POLL_MS = 5000;
// Server already returns the exact safe borrowable — no client-side buffer needed

const STATUS_LABELS = {
  NORMAL: { label: "Normal", cls: "text-success" },
  MARGIN_CALL: { label: "Margin Call", cls: "text-warning" },
  LIQUIDATING: { label: "Liquidating", cls: "text-danger" },
  LIQUIDATED: { label: "Liquidated", cls: "text-danger" },
};

function fmt(v, dp = 8) {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return "—";
  // Use toFixed to avoid scientific notation, then strip trailing zeros
  return n.toFixed(dp).replace(/\.?0+$/, "") || "0";
}

function fmtNum(v, dp = 2) {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(dp);
}

function NetBadge({ value }) {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return <span>—</span>;
  const color = n < 0 ? "#e45561" : n > 0 ? "#01bc8d" : undefined;
  return <span style={color ? { color } : undefined}>{fmt(value)}</span>;
}

function CrossMarginTableEmptyBody({ colSpan }) {
  return (
    <tr className="no-data-row no-data-row2">
      <td colSpan={colSpan}>
        <div className="no-data-wrapper">
          <div className="no_data_vector">
            <img src="/images/no_data_vector.svg" className="dark_img" alt="" />
            <img src="/images/no_data_vector_light.png" className="light_img" alt="" />
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const BORROW_REPAY_MODAL_ID = "marginBorrowRepayModal";

function openTransferBsModal() { window.$("#walletTransferModal").modal("show"); }

export default function CrossMarginAccountPage() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const versionRef = useRef(null);

  const [debts, setDebts] = useState([]);
  const [positions, setPositions] = useState([]);     // from /v1/cross/positions
  const [posSummary, setPosSummary] = useState(null);   // positions.summary (interim risk source)
  const [risk, setRisk] = useState(null);   // /v1/cross/risk (preferred)
  const [pnlData, setPnlData] = useState(null);   // /v1/cross/pnl
  const [pnlPeriod, setPnlPeriod] = useState("24h");  // 24h | 7d | 30d | all

  const [activeTab, setActiveTab] = useState("funds");   // "funds" | "positions"
  const [search, setSearch] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const searchInputRef = useRef(null);

  const searchContainerRef = useRef(null);

  const toggleSearch = useCallback(() => {
    setSearchActive((v) => !v);
  }, []);

  useEffect(() => {
    if (!searchActive) return;
    const t = window.setTimeout(() => searchInputRef.current?.focus(), 320);
    return () => window.clearTimeout(t);
  }, [searchActive]);

  useEffect(() => {
    if (!searchActive) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSearchActive(false);
    };
    const onClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setSearchActive(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [searchActive]);

  const [hideZero, setHideZero] = useState(false);
  const [debtOnly, setDebtOnly] = useState(false);

  const [borrowRepayModal, setBorrowRepayModal] = useState(null);

  const [rowPopup, setRowPopup] = useState(null);   // { type: "fund"|"position", data: row }
  const [closingPair, setClosingPair] = useState(null);  // pair string being closed (in-flight / cooldown)
  const [closePositionModal, setClosePositionModal] = useState(null); // pair string to confirm close
  const [closePositionLoading, setClosePositionLoading] = useState(false);

  const pendingAssetRef = useRef(null);

  // pairs map: symbol (e.g. "BTCUSDT") → min_notional
  const [pairsMinNotional, setPairsMinNotional] = useState({});

  // ── Fetchers ────────────────────────────────────────────────────────────────
  const fetchAccount = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    if (!quiet) setError(null);
    try {
      const res = await AuthService.crossAccount();
      if (res?.success && res.data) {
        const next = {
          ...res.data,
          assets: Array.isArray(res.data.assets) ? res.data.assets : [],
        };
        const version = res.data?.wallet_version;
        if (versionRef.current !== version) {
          versionRef.current = version;
          setAccount(next);
        } else if (!quiet) {
          setAccount(next);
        }
      } else if (!quiet) {
        setError(res?.message || "Failed to load account");
      }
    } catch (e) {
      if (!quiet) setError(e?.message || "Failed to load account");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  const fetchDebts = useCallback(async () => {
    try {
      const res = await AuthService.crossDebts();
      if (res?.success && Array.isArray(res.data?.debts)) {
        setDebts(res.data.debts);
      } else {
        setDebts([]);
      }
    } catch {
      setDebts([]);
    }
  }, []);

  const fetchPositions = useCallback(async () => {
    try {
      const res = await AuthService.crossPositions();
      if (res?.success && Array.isArray(res.data?.positions)) {
        setPositions(res.data.positions);
        setPosSummary(res.data?.summary ?? null);
      } else {
        setPositions([]);
        setPosSummary(null);
      }
    } catch {
      setPositions([]);
      setPosSummary(null);
    }
  }, []);

  const fetchRisk = useCallback(async () => {
    try {
      const res = await AuthService.crossRisk?.();
      if (res?.success && res.data) {
        setRisk(res.data);
      } else {
        setRisk(null);
      }
    } catch {
      setRisk(null);
    }
  }, []);

  const fetchPnl = useCallback(async (period) => {
    try {
      const res = await AuthService.crossPnl(period);
      if (res?.success && res.data) {
        setPnlData(res.data);
      } else {
        setPnlData(null);
      }
    } catch {
      setPnlData(null);
    }
  }, []);

  useEffect(() => { fetchPnl(pnlPeriod); }, [pnlPeriod, fetchPnl]);

  useEffect(() => {
    AuthService.getPairs?.().then((res) => {
      if (!res?.success) return;
      const map = {};
      (res.data || []).forEach((p) => {
        const sym = `${p.base_currency}${p.quote_currency}`;
        if (p.min_notional != null) map[sym] = parseFloat(p.min_notional);
      });
      setPairsMinNotional(map);
    }).catch(() => { });
  }, []);

  useEffect(() => {
    fetchAccount(false);
    fetchDebts();
    fetchPositions();
    fetchRisk();
    const ap = setInterval(() => fetchAccount(true), POLL_INTERVAL_MS);
    const dp = setInterval(fetchDebts, DEBTS_POLL_MS);
    const pp = setInterval(fetchPositions, DEBTS_POLL_MS);
    const rp = setInterval(fetchRisk, DEBTS_POLL_MS);
    const np = setInterval(() => fetchPnl(pnlPeriod), 45000);
    return () => { clearInterval(ap); clearInterval(dp); clearInterval(pp); clearInterval(rp); clearInterval(np); };
  }, [fetchAccount, fetchDebts, fetchPositions, fetchRisk, fetchPnl, pnlPeriod]);

  const refreshAll = useCallback(async () => {
    versionRef.current = null;
    await Promise.all([
      fetchAccount(true),
      fetchDebts(),
      fetchPositions(),
      fetchRisk(),
      fetchPnl(pnlPeriod),
    ]);
    setBorrowRepayModal((prev) => {
      if (!prev?.row) return prev;
      return { ...prev, row: { ...prev.row, debt: null } };
    });
  }, [fetchAccount, fetchDebts, fetchPositions, fetchRisk, fetchPnl, pnlPeriod]);

  const walletTransfer = useWalletTransfer({
    initialFrom: "spot",
    initialTo: "cross_margin",
    onTransferSuccess: async () => { refreshAll(); },
  });

  useEffect(() => {
    if (!pendingAssetRef.current || !walletTransfer.currencyData.length) return;
    const sym = pendingAssetRef.current;
    pendingAssetRef.current = null;
    const match = walletTransfer.currencyData.find(
      (c) => (c.short_name || "").toUpperCase() === sym.toUpperCase()
    );
    if (match) walletTransfer.setSelectedCurrency(match);
  }, [walletTransfer.currencyData, walletTransfer.setSelectedCurrency]);

  // Market close via POST /v1/cross/position/close — confirm modal first (same as trade page)
  const handleClosePosition = useCallback(async () => {
    const pair = typeof closePositionModal === "string" ? closePositionModal : null;
    if (!pair || closePositionLoading || closingPair === pair) return;
    setClosePositionLoading(true);
    setClosingPair(pair);
    try {
      const res = await AuthService.crossClosePosition({ pair });
      if (res?.success) {
        alertSuccessMessage(res.message || "Position close order placed.");
        setClosePositionModal(null);
        // Refresh positions, account, and debts — close may have auto-repaid debt
        refreshAll();
      } else {
        alertErrorMessage(res?.message || "Failed to close position.");
      }
    } catch (e) {
      alertErrorMessage(e?.message || "Failed to close position.");
    } finally {
      setClosePositionLoading(false);
      setClosingPair(null);
    }
  }, [closePositionModal, closePositionLoading, closingPair, refreshAll]);

  const openTransferForAsset = useCallback((assetSymbol) => {
    walletTransfer.openTransferModal({ from: "spot", to: "cross_margin" });
    if (walletTransfer.currencyData.length > 0) {
      const match = walletTransfer.currencyData.find(
        (c) => (c.short_name || "").toUpperCase() === assetSymbol.toUpperCase()
      );
      walletTransfer.setSelectedCurrency(match || walletTransfer.currencyData[0]);
    } else {
      pendingAssetRef.current = assetSymbol;
    }
    openTransferBsModal();
  }, [walletTransfer]);

  // ── Derived values ──────────────────────────────────────────────────────────
  const assets = account?.assets ?? [];
  const statusInfo = STATUS_LABELS[account?.status] ?? { label: account?.status ?? "—", cls: "" };
  const debtByAsset = Object.fromEntries(debts.map((d) => [d.asset, d]));

  const closeBorrowRepayModal = useCallback(() => {
    window.$(`#${BORROW_REPAY_MODAL_ID}`).modal("hide");
    setBorrowRepayModal(null);
  }, []);

  const openBorrowRepayModal = useCallback((assetSymbol, tab = "borrow") => {
    const sym = String(assetSymbol || "").toUpperCase();
    const assetRow = assets.find((a) => String(a.asset || "").toUpperCase() === sym);
    const debt = debtByAsset[assetRow?.asset || assetSymbol] || null;
    const row = {
      base: assetRow?.asset || assetSymbol,
      currency_id: assetRow?.currency_id,
      freeBalance: assetRow?.balance ?? "0",
      debt,
      pair: `${assetRow?.asset || assetSymbol}/USDT`,
    };
    setBorrowRepayModal({ row, tab });
    setTimeout(() => window.$(`#${BORROW_REPAY_MODAL_ID}`).modal("show"), 0);
  }, [assets, debtByAsset]);

  // Risk data: prefer /v1/cross/risk; fall back to positions.summary
  const riskSrc = risk ?? posSummary ?? {};
  const riskStatus = risk?.status ?? account?.status ?? null;
  const totalDebtUsd = riskSrc.total_liability ?? riskSrc.total_debt_usd ?? null;
  const accountEquityUsd = riskSrc.net_equity ?? riskSrc.account_equity_usd ?? null;
  const totalBalanceUsdt = riskSrc.total_asset_value ?? riskSrc.total_balance_usdt ?? null;
  const marginLevel = riskSrc.margin_level != null ? parseFloat(riskSrc.margin_level) : null;
  const marginCallLevel = riskSrc.margin_call_level != null ? parseFloat(riskSrc.margin_call_level) : null;
  const liquidationLevel = riskSrc.liquidation_level != null ? parseFloat(riskSrc.liquidation_level) : null;
  const remainingBorrow = riskSrc.remaining_borrow_value != null ? parseFloat(riskSrc.remaining_borrow_value) : null;
  const maxLeverageRisk = riskSrc.max_leverage ?? account?.max_leverage ?? null;
  const borrowMarginLvl = riskSrc.borrow_margin_level != null ? parseFloat(riskSrc.borrow_margin_level) : marginCallLevel;
  const transferOutLvl = riskSrc.transfer_out_margin_level != null ? parseFloat(riskSrc.transfer_out_margin_level) : null;
  // PnL from /v1/cross/pnl
  const pnlNetRealized = pnlData?.net_realized_pnl != null ? parseFloat(pnlData.net_realized_pnl) : null;
  const pnlUnrealized = pnlData?.unrealized_pnl != null ? parseFloat(pnlData.unrealized_pnl) : null;
  const pnlRealized = pnlData?.realized_pnl != null ? parseFloat(pnlData.realized_pnl) : null;
  const pnlInterest = pnlData?.interest_paid != null ? parseFloat(pnlData.interest_paid) : null;
  const pnlClosedCount = pnlData?.closed_positions ?? null;

  // Fallback total debt from debts list
  const fallbackDebt = debts.reduce((s, d) => s + parseFloat(d.principal || 0) + parseFloat(d.interest_accrued || 0), 0);

  const hasBorrowed = assets.some((a) => parseFloat(a.borrowed) > 0) || debts.length > 0;

  const filtered = assets.filter((a) => {
    if (debtOnly && parseFloat(a.borrowed) <= 0) return false;
    if (hideZero && parseFloat(a.balance) <= 0 && parseFloat(a.borrowed) <= 0) return false;
    const q = search.trim().toLowerCase();
    if (q && !a.asset.toLowerCase().includes(q)) return false;
    return true;
  });

  const filteredPositions = positions.filter((p) => {
    if (hideZero && Math.abs(parseFloat(p.net_quantity || 0)) < 0.00001) return false;
    const q = search.trim().toLowerCase();
    if (q && !p.asset.toLowerCase().includes(q)) return false;
    return true;
  });

  // Show skeleton on first load (account not yet received)
  if (loading && !account) {
    return <WalletOverviewSkeleton variant="cross-margin-account" />;
  }

  return (
    <>
      <div className="dashboard_right funding-account">

        <div className="funding-account__header border-0">
          <div>
            <h1 className="funding-account__title">Cross Margin Account</h1>
          </div>

        </div>

        {/* ── Summary card ── */}
        <div className="funding-account__summarycard border-0">

          {/* Desktop row 1: balance | gauge | leverage — hidden on mobile */}
          <div className="d-none d-md-flex align-items-start justify-content-between gap-4 mb-3 crossmargintop">
            <div>
              <div className="funding-account__totals-label cmp-mb-4">Total balance</div>
              <div className="d-flex align-items-baseline gap-2">
                <div className="funding-account__summary-num cmp-balance-val">
                  {totalBalanceUsdt != null ? fmtNum(totalBalanceUsdt, 8) : "—"}
                </div>
                <div className="cmp-currency-label">USDT</div>
              </div>
              {totalBalanceUsdt != null && <div className="cmp-balance-fiat">≈ ${fmtNum(totalBalanceUsdt, 2)}</div>}
            </div>
            <div className="d-flex flex-column align-items-center">
              <div className="cmp-margin-gauge-label">Margin Level</div>
              <CrossMarginLevelGauge
                level={marginLevel}
                mmr={account?.maintenance_margin_rate ? 1 + account.maintenance_margin_rate : 1.1}
                warningRate={account?.warning_margin_rate ? 1 + account.warning_margin_rate : 1.15}
              />
            </div>
            <div className="d-flex flex-column align-items-end gap-2">
              <div className="cmp-flex-center-gap-6">
                {/* <span className="cmp-cross-leverage">Cross {maxLeverageRisk ?? "—"}x</span> */}
                {/* <i className="ri-settings-4-line cmp-settings-icon" /> */}
              </div>
              <span className={`badge cmp-risk-badge ${riskStatus === "NORMAL" ? "bg-success" : riskStatus === "MARGIN_CALL" ? "bg-warning text-dark" : riskStatus ? "bg-danger" : "bg-secondary"}`}>
                {riskStatus ?? "—"}
              </span>
            </div>
          </div>

          {/* Mobile row 1: balance (full width) — shown only on mobile */}
          <div className="d-md-none mb-3">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <div className="funding-account__totals-label">Total balance</div>
              <div className="cmp-flex-center-gap-6">
                <span className="cmp-cross-leverage-mobile">Cross {maxLeverageRisk ?? "—"}x</span>
                <span className={`badge cmp-risk-badge-mobile ${riskStatus === "NORMAL" ? "bg-success" : riskStatus === "MARGIN_CALL" ? "bg-warning text-dark" : riskStatus ? "bg-danger" : "bg-secondary"}`}>
                  {riskStatus ?? "—"}
                </span>
              </div>
            </div>
            <div className="d-flex align-items-baseline gap-2">
              <div className="funding-account__summary-num cmp-balance-val-mobile">
                {totalBalanceUsdt != null ? fmtNum(totalBalanceUsdt, 8) : "—"}
              </div>
              <div className="cmp-currency-label">USDT</div>
            </div>
            {totalBalanceUsdt != null && <div className="cmp-balance-fiat">≈ ${fmtNum(totalBalanceUsdt, 2)}</div>}
          </div>

          <div className="d-flex gap-4 margin_pnl_bl">

            <div className="funding_acount_block_margin">

              {/* Row 2: Margin Level | Account Equity */}
              <div className="funding-account__summary-grid mt-2">
                <div className="funding-account__summary-stat">
                  <div className="funding-account__summary-stat-k">Margin Level</div>
                  <div className={`funding-account__summary-stat-v ${marginLevel != null && marginLevel < 1.1 ? "cmp-text-danger" : marginLevel != null && marginLevel < 1.3 ? "cmp-text-warning" : "cmp-text-inherit"}`}>
                    {marginLevel != null ? fmtNum(marginLevel, 4) : "—"}
                  </div>
                  {marginCallLevel != null && <div className="cmp-stat-subtext">Call at {fmtNum(marginCallLevel, 2)} · Liq. at {liquidationLevel != null ? fmtNum(liquidationLevel, 2) : "—"}</div>}
                </div>
                <div className="funding-account__summary-stat funding-account__summary-stat--right">
                  <div className="funding-account__summary-stat-k">Account Equity (USDT)</div>
                  <div className="funding-account__summary-stat-v cmp-stat-val">
                    {accountEquityUsd != null ? fmtNum(accountEquityUsd, 4) : "—"}
                  </div>
                  {accountEquityUsd != null && <div className="cmp-stat-subtext">≈ ${fmtNum(accountEquityUsd, 2)}</div>}
                </div>
              </div>

              {/* Row 3: Total Debt | Remaining Borrow */}
              <div className="funding-account__summary-grid mt-2">
                <div className="funding-account__summary-stat">
                  <div className="funding-account__summary-stat-k">Total Debt (USDT)</div>
                  <div className="funding-account__summary-stat-v cmp-stat-val">
                    {totalDebtUsd != null ? fmtNum(totalDebtUsd, 4) : fallbackDebt > 0 ? fmtNum(fallbackDebt, 4) : "—"}
                  </div>
                  {(totalDebtUsd != null || fallbackDebt > 0) && <div className="cmp-stat-subtext">≈ ${fmtNum(totalDebtUsd ?? fallbackDebt, 2)}</div>}
                </div>
                <div className="funding-account__summary-stat funding-account__summary-stat--right">
                  <div className="funding-account__summary-stat-k">Remaining Borrowable (USDT)</div>
                  <div className="funding-account__summary-stat-v cmp-stat-val">
                    {remainingBorrow != null ? fmtNum(remainingBorrow, 4) : "—"}
                  </div>
                  {remainingBorrow != null && <div className="cmp-stat-subtext">≈ ${fmtNum(remainingBorrow, 2)}</div>}
                </div>
              </div>

            </div>

            {/* Mobile row 4: margin level gauge — hidden on desktop */}
            <div className="d-md-none mt-3 cmp-mobile-row-divider">
              <div className="cmp-mobile-margin-gauge-title">Margin Level</div>
              <div className="d-flex align-items-center gap-3">
                <CrossMarginLevelGauge
                  level={marginLevel}
                  mmr={marginCallLevel ?? (account?.maintenance_margin_rate ? 1 + account.maintenance_margin_rate : 1.1)}
                  warningRate={liquidationLevel ?? (account?.warning_margin_rate ? 1 + account.warning_margin_rate : 1.15)}
                />
                <div>
                  <div className={`cmp-mobile-margin-gauge-val ${marginLevel != null && marginLevel < 1.1 ? "cmp-text-danger" : marginLevel != null && marginLevel < 1.3 ? "cmp-text-warning" : "cmp-text-inherit"}`}>
                    {marginLevel != null ? fmtNum(marginLevel, 4) : "—"}
                  </div>
                  {marginCallLevel != null && <div className="cmp-stat-subtext">Call {fmtNum(marginCallLevel, 2)} · Liq {liquidationLevel != null ? fmtNum(liquidationLevel, 2) : "—"}</div>}
                </div>
              </div>
            </div>

            {/* PnL widget — shared desktop/mobile */}
            <div className="funding_acount_block_margin">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="cmp-pnl-title">PnL</div>
                <div className="cmp-pnl-btn-group">
                  {["24h", "7d", "30d", "all"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPnlPeriod(p)}
                      className={`cmp-pnl-btn ${pnlPeriod === p ? 'active' : ''}`}
                    >
                      {p === "all" ? "All" : p}
                    </button>
                  ))}
                </div>
              </div>
              {pnlData ? (
                <div className="cmp-pnl-grid">
                  {[
                    { label: "Net Realized PnL", value: pnlNetRealized },
                    { label: "Unrealized PnL", value: pnlUnrealized },
                    { label: "Realized PnL", value: pnlRealized },
                    { label: "Interest Paid", value: pnlInterest, invert: true },
                  ].map(({ label, value, invert }) => {
                    const n = value ?? 0;
                    const isPos = invert ? n <= 0 : n >= 0;
                    const colorClass = n === 0 ? "cmp-text-muted" : isPos ? "cmp-text-success" : "cmp-text-danger";
                    return (
                      <div key={label}>
                        <div className="cmp-pnl-label">{label}</div>
                        <div className={`cmp-pnl-val ${colorClass}`}>
                          {n >= 0 ? "+" : ""}{fmtNum(n, 4)} USDT
                        </div>
                      </div>
                    );
                  })}
                  {pnlClosedCount != null && (
                    <div className="cmp-pnl-footer">
                      {pnlClosedCount} position{pnlClosedCount !== 1 ? "s" : ""} closed in this period
                    </div>
                  )}
                </div>
              ) : (
                <div className="cmp-loading-text">Loading PnL…</div>
              )}
            </div>

          </div>

          {/* Mobile action buttons row — hidden on desktop */}
          <div className="d-md-none mt-3 cmp-mobile-row-divider buttons_row">
            <div className="cmp-mobile-actions-row">
              <button
                type="button"
                className="funding-account__pill funding-account__pill--primary cmp-flex-1-center"
                onClick={() => openBorrowRepayModal(assets[0]?.asset || "BTC", "borrow")}
              >
                Borrow
              </button>
              <button
                type="button"
                className="funding-account__pill cmp-flex-1-center"
                onClick={() => {
                  const firstDebt = debts[0] ?? assets.find((a) => parseFloat(a.borrowed) > 0);
                  openBorrowRepayModal(firstDebt ? firstDebt.asset : (assets[0]?.asset || "BTC"), "repay");
                }}
              >
                Repay
              </button>
              <button
                type="button"
                className="funding-account__pill cmp-flex-1-center"
                onClick={() => { walletTransfer.openTransferModal({ from: "spot", to: "cross_margin" }); openTransferBsModal(); }}
              >
                Transfer
              </button>
              <button
                type="button"
                className="agce-ao-more-btn flex-shrink-0"
                aria-label="More options"
                onClick={() => { walletTransfer.openTransferModal({ from: "spot", to: "cross_margin" }); openTransferBsModal(); }}
              >
                <i className="ri-more-2-fill" aria-hidden="true" />
              </button>
            </div>
          </div>

        </div>

        {/* ── Funds / Positions tabs ── */}
        <div className="funding-account__card mt-3">

          {/* Tab header row */}
          <div className="d-flex align-items-center justify-content-between cmp-tabs-header crossmargintop_pad_btm">

            {/* Tab labels */}
            <div className="cmp-tabs-group">
              {["funds", "positions"].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => { setActiveTab(tab); setSearch(""); setHideZero(false); setDebtOnly(false); }}
                  className={`cmp-tab-btn ${activeTab === tab ? 'active' : ''}`}
                >
                  {tab === "funds" ? "Funds" : "Positions"}
                </button>
              ))}
            </div>

            {/* Desktop right controls — hidden below md */}

            <div className="right_crossmargin d-flex gap-3">

              <div className="d-none d-md-flex align-items-center gap-3 flex-wrap cmp-py-8">
                <div
                  ref={searchContainerRef}
                  className={`searchBar custom-tabs cmp-search-box ${searchActive ? "search_active" : ""}`}
                  onClick={toggleSearch}
                >
                  <i className="ri-search-2-line" aria-hidden="true" />
                  <input
                    ref={searchInputRef}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={activeTab === "funds" ? "Search asset" : "Search coin"}
                    type="search"
                    className="custom_search"
                    aria-label="Search"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {activeTab === "funds" ? (
                  <>
                    <label className="funding-account__check">
                      <input type="checkbox" checked={hideZero} onChange={(e) => setHideZero(e.target.checked)} />
                      Hide small accounts
                    </label>
                    <label className="funding-account__check">
                      <input type="checkbox" checked={debtOnly} onChange={(e) => setDebtOnly(e.target.checked)} />
                      Only show debts
                    </label>
                  </>
                ) : (
                  <label className="funding-account__check">
                    <input type="checkbox" checked={hideZero} onChange={(e) => setHideZero(e.target.checked)} />
                    Hide low positions
                  </label>
                )}
                <button type="button" className="funding-account__pill"
                  onClick={() => { walletTransfer.openTransferModal({ from: "spot", to: "cross_margin" }); openTransferBsModal(); }}>
                  Transfer
                </button>
                <button type="button" className="funding-account__pill funding-account__pill--primary"
                  onClick={() => openBorrowRepayModal(assets[0]?.asset || "BTC", "borrow")}>
                  Borrow
                </button>
              </div>

              {/* Mobile right controls — filter icon only, visible below md */}
              <div className="d-flex d-md-none align-items-center gap-2 cmp-py-8">
                {/* Filter icon — opens filter modal (contains search, checkboxes, Transfer, Borrow) */}
                <SoMobileFilters
                  modalId={`cross-margin-filters-${activeTab}`}
                  title={activeTab === "funds" ? "Fund Filters" : "Position Filters"}
                  onReset={() => { setSearch(""); setHideZero(false); setDebtOnly(false); }}
                  toolbar={
                    <div className="cmp-mobile-filter-body">
                      {/* Search */}
                      <div className="searchBar custom-tabs cmp-w-100">
                        <i className="ri-search-2-line" aria-hidden="true" />
                        <input
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder={activeTab === "funds" ? "Search asset" : "Search coin"}
                          type="search"
                          aria-label="Search"
                          className="custom_search cmp-w-100"
                        />
                      </div>

                      {/* Checkboxes */}
                      {activeTab === "funds" ? (
                        <>
                          <label className="funding-account__check cmp-checkbox-label">
                            <input type="checkbox" checked={hideZero} onChange={(e) => setHideZero(e.target.checked)} />
                            Hide small accounts
                          </label>
                          <label className="funding-account__check cmp-checkbox-label">
                            <input type="checkbox" checked={debtOnly} onChange={(e) => setDebtOnly(e.target.checked)} />
                            Only show debts
                          </label>
                        </>
                      ) : (
                        <label className="funding-account__check cmp-checkbox-label">
                          <input type="checkbox" checked={hideZero} onChange={(e) => setHideZero(e.target.checked)} />
                          Hide low positions
                        </label>
                      )}

                      {/* Action buttons */}
                      <div className="cmp-mobile-filter-actions">
                        <button
                          type="button"
                          className="funding-account__pill cmp-flex-1"
                          data-bs-dismiss="modal"
                          onClick={() => { walletTransfer.openTransferModal({ from: "spot", to: "cross_margin" }); openTransferBsModal(); }}
                        >
                          Transfer
                        </button>
                        <button
                          type="button"
                          className="funding-account__pill funding-account__pill--primary cmp-flex-1"
                          data-bs-dismiss="modal"
                          onClick={() => openBorrowRepayModal(assets[0]?.asset || "BTC", "borrow")}
                        >
                          Borrow
                        </button>
                      </div>
                    </div>
                  }
                />
              </div>

            </div>

          </div>

          {/* ── FUNDS TAB ── */}
          {activeTab === "funds" && (
            <div className="funding-account__tablewrap cmp-table-wrap">
              {loading && !account ? (
                <div className="text-center py-5 text-muted">
                  <div className="spinner-border spinner-border-sm me-2" role="status" />Loading account…
                </div>
              ) : error && !account ? (
                <div className="text-center py-5">
                  <p className="text-danger mb-3">{error}</p>
                  <button type="button" className="btn btn-sm btn-primary" onClick={() => fetchAccount(false)}>Retry</button>
                </div>
              ) : (
                <table className="funding-account__table">
                  <thead>
                    <tr>
                      <th>Coin</th>
                      <th className="ta-r">Total Balance</th>
                      <th className="ta-r d-none d-md-table-cell">Available</th>
                      <th className="ta-r d-none d-md-table-cell">In Order</th>
                      <th className="ta-r d-none d-md-table-cell">Borrowed</th>
                      <th className="ta-r">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <CrossMarginTableEmptyBody colSpan={6} />
                    ) : filtered.map((a) => {
                      const hasBorrow = parseFloat(a.borrowed) > 0;
                      const debt = debtByAsset[a.asset] || null;
                      const totalBal = (parseFloat(a.balance || 0) + parseFloat(a.locked || 0)).toFixed(8).replace(/\.?0+$/, "");
                      return (
                        <tr key={a.currency_id || a.asset}>
                          <td data-label="Coin">
                            <div>
                              <span className="funding-account__coin-sym">{a.asset}</span>
                              {/* Interest badge — visible at a glance when non-zero */}
                              {debt?.interest_accrued && parseFloat(debt.interest_accrued) > 0 && (
                                <div className="funding-account__subnum cmp-interest-badge">
                                  +{fmt(debt.interest_accrued)} interest
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="ta-r" data-label="Total Balance">
                            <div className="funding-account__num">{fmt(totalBal)}</div>
                          </td>
                          <td className="ta-r d-none d-md-table-cell" data-label="Available">
                            <div className="funding-account__num">{fmt(a.balance)}</div>
                          </td>
                          <td className="ta-r d-none d-md-table-cell" data-label="Available">
                            <div className="funding-account__num">{fmt(a.locked)}</div>
                          </td>
                          <td className="ta-r d-none d-md-table-cell" data-label="Borrowed">
                            <div className={`funding-account__num ${hasBorrow ? "has-borrow cmp-borrowed-val" : ""}`}>
                              {hasBorrow ? fmt(a.borrowed) : "—"}
                            </div>
                          </td>
                          <td className="ta-r" data-label="Action">
                            <div className="funding-account__actions">
                              
                              <button type="button" className="funding-account__action funding-account__action--btn d-none d-md-inline-flex"
                                onClick={() => navigate(`/cross/${a.asset.replace("/", "_")}`)}>Trade</button>
                              <button type="button" className="funding-account__action funding-account__action--btn d-none d-md-inline-flex"
                                onClick={() => openTransferForAsset(a.asset)}>Transfer</button>
                              <button type="button" className="funding-account__action funding-account__action--btn d-none d-md-inline-flex"
                                onClick={() => openBorrowRepayModal(a.asset, hasBorrow ? "repay" : "borrow")}>
                                Borrow/Repay
                              </button>
                              {/* ⋯ always visible — opens full details sheet */}
                              <button
                                type="button"
                                className="agce-ao-more-btn flex-shrink-0"
                                aria-label="View details"
                                onClick={() => setRowPopup({ type: "fund", data: { ...a, debt } })}
                              >
                                <i className="ri-more-2-fill" aria-hidden="true" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ── POSITIONS TAB ── */}
          {activeTab === "positions" && (
            <div className="funding-account__tablewrap cmp-table-wrap">
              <table className="funding-account__table">
                <thead>
                  <tr>
                    <th>Coin</th>
                    <th className="ta-r d-none d-md-table-cell">Size</th>
                    <th className="ta-r">Value (USDT)</th>
                    <th className="ta-r d-none d-md-table-cell">Entry Price</th>
                    <th className="ta-r d-none d-md-table-cell">PnL / ROE</th>
                    <th className="ta-r">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPositions.length === 0 ? (
                    <CrossMarginTableEmptyBody colSpan={6} />
                  ) : filteredPositions.map((p) => {
                    const isLong = p.side === "LONG";
                    const sideColor = isLong ? "#01bc8d" : "#e45561";
                    const valNum = parseFloat(p.value_usdt || 0);
                    const pnl = parseFloat(p.unrealized_pnl || 0);
                    const roe = parseFloat(p.roe_pct || 0);
                    const pnlColor = pnl >= 0 ? "#01bc8d" : "#e45561";
                    const assetRow = assets.find((a) => a.asset === p.asset);
                    const debt = debtByAsset[p.asset] || null;
                    return (
                      <tr key={p.currency_id || p.asset}>
                        <td data-label="Coin">
                          <div>
                            <div className="d-flex align-items-center gap-1">
                              <span className="funding-account__coin-sym">{p.asset}</span>
                              <span className={`cmp-side-badge cmp-side-badge--${isLong ? 'long' : 'short'}`}>
                                {isLong ? "L" : "S"}
                              </span>
                            </div>
                            {/* Mark price hint on mobile */}
                            {p.mark_price != null && (
                              <div className="funding-account__subnum d-md-none cmp-stat-subtext">
                                Mark: {fmtNum(p.mark_price, 2)}
                              </div>
                            )}
                            {/* PnL hint on mobile */}
                            {p.unrealized_pnl != null && (
                              <div className={`funding-account__subnum d-md-none ${pnl >= 0 ? "cmp-text-success" : "cmp-text-danger"}`}>
                                {pnl >= 0 ? "+" : ""}{fmtNum(pnl, 4)} ({roe >= 0 ? "+" : ""}{fmtNum(roe, 2)}%)
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="ta-r d-none d-md-table-cell" data-label="Size">
                          <div className={`funding-account__num ${isLong ? "cmp-text-success" : "cmp-text-danger"}`}>
                            {fmt(p.net_quantity)}
                          </div>
                          <div className="funding-account__subnum cmp-stat-subtext">
                            {p.asset}
                          </div>
                        </td>
                        <td className="ta-r" data-label="Value (USDT)">
                          <div className={`funding-account__num ${valNum !== 0 ? (isLong ? "cmp-text-success" : "cmp-text-danger") : ""}`}>
                            {valNum !== 0 ? fmtNum(Math.abs(valNum), 2) : "0.00"}
                          </div>
                          {p.mark_price != null && (
                            <div className="funding-account__subnum d-none d-md-block cmp-stat-subtext">
                              Mark: {fmtNum(p.mark_price, 2)}
                            </div>
                          )}
                        </td>
                        <td className="ta-r d-none d-md-table-cell" data-label="Entry Price">
                          <div className="funding-account__num">
                            {(p.entry_price == null || parseFloat(p.entry_price) === 0) ? "—" : fmtNum(p.entry_price, 2)}
                          </div>
                        </td>
                        <td className="ta-r d-none d-md-table-cell" data-label="PnL / ROE">
                          <div className={`funding-account__num ${pnl >= 0 ? "cmp-text-success" : "cmp-text-danger"} fw-bold`}>
                            {pnl >= 0 ? "+" : ""}{fmtNum(pnl, 4)}
                          </div>
                          <div className={`funding-account__subnum ${pnl >= 0 ? "cmp-text-success" : "cmp-text-danger"}`}>
                            {roe >= 0 ? "+" : ""}{fmtNum(roe, 2)}%
                          </div>
                        </td>
                        <td className="ta-r" data-label="Action">
                          <div className="funding-account__actions">
                            {/* Close position — uses server-side sizing */}
                            {(() => {
                              if (!p.position_id) return (
                                <span className="cmp-relative-inline agce-no-liab-wrap d-none d-md-inline-flex">
                                  <span className="cmp-help-text">—</span>
                                  <span className="agce-no-liab-tip">There is no liability on this position</span>
                                </span>
                              );
                              const posVal = Math.abs(parseFloat(p.value_usdt || 0));
                              const pairKey = p.pair || `${p.asset}USDT`;
                              const minNotional = pairsMinNotional[pairKey] ?? 10;
                              const tooSmall = posVal > 0 && posVal < minNotional;
                              const isClosing = closingPair === pairKey;
                              const closeBtn = (
                                <button
                                  type="button"
                                  className={`funding-account__action funding-account__action--btn d-none d-md-inline-flex cmp-close-btn ${tooSmall ? 'disabled' : ''}`}
                                  disabled={tooSmall || isClosing}
                                  onClick={() => { if (!tooSmall) setClosePositionModal(pairKey); }}
                                >
                                  {isClosing ? "Closing…" : "Close"}
                                </button>
                              );
                              return tooSmall ? (
                                <span className="agce-no-liab-wrap d-none d-md-inline-flex">
                                  {closeBtn}
                                  <span className="agce-no-liab-tip">Only supports positions with a value of more than or equal to {minNotional} USDT. Try transfer to spot.</span>
                                </span>
                              ) : closeBtn;
                            })()}
                            <button type="button" className="funding-account__action funding-account__action--btn d-none d-md-inline-flex"
                              onClick={() => openTransferForAsset(p.asset)}>Transfer</button>
                            <button type="button" className="funding-account__action funding-account__action--btn d-none d-md-inline-flex"
                              onClick={() => openBorrowRepayModal(p.asset, debt ? "repay" : "borrow")}>
                              {debt ? "Repay" : "Borrow"}
                            </button>
                            {/* ⋯ always visible */}
                            <button
                              type="button"
                              className="agce-ao-more-btn flex-shrink-0"
                              aria-label="View details"
                              onClick={() => setRowPopup({ type: "position", data: { ...p, assetRow, debt } })}
                            >
                              <i className="ri-more-2-fill" aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

        </div>

      </div>

      {/* ── Mobile row detail sheet ── */}
      {rowPopup && (() => {
        const isFund = rowPopup.type === "fund";
        const d = rowPopup.data;
        const isLong = d.side === "LONG";
        const sideColor = isLong ? "#01bc8d" : "#e45561";
        const hasBorrow = parseFloat(d.borrowed || 0) > 0;
        const debtRow = d.debt || null;
        const totalBal = isFund
          ? ((parseFloat(d.balance || 0) + parseFloat(d.locked || 0)).toFixed(8).replace(/\.?0+$/, ""))
          : null;

        return (
          <>
            <div className="agce-ao-detail-backdrop" role="presentation" onClick={() => setRowPopup(null)} />
            <div
              className="modal fade show d-flex agce-ao-sheet-modal cmp-z-1060"
              tabIndex="-1"
              role="dialog"
              aria-modal="true"
              aria-labelledby="crossRowPopupTitle"
            >
              <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable agce-ao-sheet-dialog">
                <div className="modal-content agce-ao-sheet border-0">
                  <h2 id="crossRowPopupTitle" className="visually-hidden">{d.asset} details</h2>

                  {/* Sheet header */}
                  <div className="agce-ao-sheet__header">
                    <div className="agce-ao-sheet__brand">
                      <div>
                        <div className="agce-ao-sheet__sym">{d.asset}</div>
                        {!isFund && (
                          <div className="agce-ao-sheet__fullname">
                            <span className={`cmp-side-badge cmp-side-badge--${isLong ? 'long' : 'short'}`}>
                              {isLong ? "LONG" : "SHORT"}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <button type="button" className="btn-close agce-ao-sheet__close" aria-label="Close" onClick={() => setRowPopup(null)} />
                  </div>

                  <div className="modal-body agce-ao-sheet__body">
                    {/* Hero value */}
                    <div className="agce-ao-sheet__hero">
                      {isFund ? (
                        <>
                          <div className="agce-ao-sheet__hero-num"><NetBadge value={d.net} /> {d.asset}</div>
                          <div className="agce-ao-sheet__hero-fiat">Net Asset</div>
                        </>
                      ) : (() => {
                        const heroPnl = parseFloat(d.unrealized_pnl || 0);
                        const heroRoe = parseFloat(d.roe_pct || 0);
                        const heroColor = heroPnl >= 0 ? "#01bc8d" : "#e45561";
                        return (
                          <>
                            <div className="agce-ao-sheet__hero-num" style={{ color: sideColor }}>
                              {fmt(d.net_quantity)} {d.asset}
                            </div>
                            <div className="agce-ao-sheet__hero-fiat">
                              ≈ {fmtNum(Math.abs(parseFloat(d.value_usdt || 0)), 2)} USDT
                            </div>
                            {d.unrealized_pnl != null && (
                              <div style={{ fontSize: 13, marginTop: 4, color: heroColor, fontWeight: 600 }}>
                                {heroPnl >= 0 ? "+" : ""}{fmtNum(heroPnl, 4)} USDT
                                <span style={{ fontSize: 11, marginLeft: 6 }}>
                                  ({heroRoe >= 0 ? "+" : ""}{fmtNum(heroRoe, 2)}%)
                                </span>
                              </div>
                            )}
                          </>
                        );
                      })()
                      }
                    </div>

                    {/* KV list */}
                    <div className="agce-ao-sheet__kvlist">
                      {isFund ? (
                        <>
                          <div className="agce-ao-sheet__kv">
                            <span>Total Balance</span>
                            <span className="agce-ao-sheet__kv-val">{fmt(totalBal)} {d.asset}</span>
                          </div>
                          <div className="agce-ao-sheet__kv">
                            <span>Available</span>
                            <span className="agce-ao-sheet__kv-val">{fmt(d.balance)} {d.asset}</span>
                          </div>
                          <div className="agce-ao-sheet__kv">
                            <span>Locked</span>
                            <span className="agce-ao-sheet__kv-val">{parseFloat(d.locked) > 0 ? `${fmt(d.locked)} ${d.asset}` : "—"}</span>
                          </div>
                          <div className="agce-ao-sheet__kv">
                            <span>Borrowed</span>
                            <span className="agce-ao-sheet__kv-val" style={hasBorrow ? { color: "#e45561" } : undefined}>
                              {hasBorrow ? `${fmt(d.borrowed)} ${d.asset}` : "—"}
                            </span>
                          </div>
                          <div className="agce-ao-sheet__kv">
                            <span>Accrued Interest</span>
                            <span className="agce-ao-sheet__kv-val" style={debtRow?.interest_accrued && parseFloat(debtRow.interest_accrued) > 0 ? { color: "#e45561" } : undefined}>
                              {debtRow?.interest_accrued && parseFloat(debtRow.interest_accrued) > 0
                                ? `${fmt(debtRow.interest_accrued)} ${d.asset}`
                                : "—"}
                            </span>
                          </div>
                          <div className="agce-ao-sheet__kv">
                            <span>Net</span>
                            <span className="agce-ao-sheet__kv-val"><NetBadge value={d.net} /> {d.asset}</span>
                          </div>
                        </>
                      ) : (() => {
                        const posPnl = parseFloat(d.unrealized_pnl || 0);
                        const posRoe = parseFloat(d.roe_pct || 0);
                        const pnlColor = posPnl >= 0 ? "#01bc8d" : "#e45561";
                        return (
                          <>
                            <div className="agce-ao-sheet__kv">
                              <span>Size</span>
                              <span className="agce-ao-sheet__kv-val" style={{ color: sideColor }}>
                                {fmt(d.net_quantity)} {d.asset}
                              </span>
                            </div>
                            <div className="agce-ao-sheet__kv">
                              <span>Position Value</span>
                              <span className="agce-ao-sheet__kv-val">
                                {fmtNum(Math.abs(parseFloat(d.value_usdt || 0)), 4)} USDT
                              </span>
                            </div>
                            <div className="agce-ao-sheet__kv">
                              <span>Entry Price</span>
                              <span className="agce-ao-sheet__kv-val">
                                {(d.entry_price == null || parseFloat(d.entry_price) === 0) ? "—" : `${fmtNum(d.entry_price, 2)} USDT`}
                              </span>
                            </div>
                            <div className="agce-ao-sheet__kv">
                              <span>Mark Price</span>
                              <span className="agce-ao-sheet__kv-val">
                                {d.mark_price != null ? fmtNum(d.mark_price, 2) : "—"} USDT
                              </span>
                            </div>
                            <div className="agce-ao-sheet__kv">
                              <span>Unrealized PnL</span>
                              <span className="agce-ao-sheet__kv-val" style={{ color: pnlColor }}>
                                {posPnl >= 0 ? "+" : ""}{fmtNum(posPnl, 4)} USDT
                              </span>
                            </div>
                            <div className="agce-ao-sheet__kv">
                              <span>ROE</span>
                              <span className="agce-ao-sheet__kv-val" style={{ color: pnlColor }}>
                                {posRoe >= 0 ? "+" : ""}{fmtNum(posRoe, 2)}%
                              </span>
                            </div>
                            <div className="agce-ao-sheet__kv">
                              <span>Realized PnL</span>
                              <span className="agce-ao-sheet__kv-val">
                                {fmtNum(parseFloat(d.realized_pnl || 0), 4)} USDT
                              </span>
                            </div>
                            {d.liquidation_price != null && (
                              <div className="agce-ao-sheet__kv">
                                <span>Liquidation Price</span>
                                <span className="agce-ao-sheet__kv-val" style={{ color: "#f59e0b" }}>
                                  {fmtNum(d.liquidation_price, 2)} USDT
                                </span>
                              </div>
                            )}
                            {d.assetRow && (
                              <div className="agce-ao-sheet__kv">
                                <span>Free Balance</span>
                                <span className="agce-ao-sheet__kv-val">{fmt(d.assetRow.balance)} {d.asset}</span>
                              </div>
                            )}
                          </>
                        );
                      })()
                      }
                    </div>

                    <div className="agce-ao-sheet__divider" />

                    {/* Action buttons in sheet */}
                    <div className="agce-ao-sheet__table-actions">
                      {/* Close position button — only for position rows */}
                      {!isFund && (() => {
                        if (!d.position_id) return (
                          <span style={{ position: "relative", display: "inline-block" }} className="agce-no-liab-wrap">
                            <span style={{ cursor: "help", color: "#8e949e", fontSize: 13, padding: "8px 4px", display: "inline-block" }}>—</span>
                            <span className="agce-no-liab-tip">There is no liability on this position</span>
                          </span>
                        );
                        const posVal = Math.abs(parseFloat(d.value_usdt || 0));
                        const mPairKey = d.pair || `${d.asset}USDT`;
                        const mMinNotional = pairsMinNotional[mPairKey] ?? 10;
                        const tooSmall = posVal > 0 && posVal < mMinNotional;
                        const mobileCloseBtn = (
                          <button
                            type="button"
                            className="funding-account__action funding-account__action--btn"
                            style={{ color: "#e45561", borderColor: "#e45561", opacity: tooSmall ? 0.5 : 1, cursor: tooSmall ? "not-allowed" : "pointer", pointerEvents: tooSmall ? "none" : "auto" }}
                            disabled={tooSmall || !!closingPair}
                            onClick={() => {
                              if (tooSmall) return;
                              setRowPopup(null);
                              setClosePositionModal(mPairKey);
                            }}
                          >
                            {closingPair === mPairKey ? "Closing…" : "Close Position"}
                          </button>
                        );
                        return tooSmall ? (
                          <span className="agce-no-liab-wrap">
                            {mobileCloseBtn}
                            <span className="agce-no-liab-tip">Only supports positions with a value of more than or equal to {mMinNotional} USDT. Try transfer to spot.</span>
                          </span>
                        ) : mobileCloseBtn;
                      })()}
                      <button
                        type="button"
                        className="funding-account__action funding-account__action--btn"
                        onClick={() => { setRowPopup(null); openTransferForAsset(d.asset); }}
                      >
                        Transfer
                      </button>
                      <button
                        type="button"
                        className="funding-account__action funding-account__action--btn"
                        onClick={() => {
                          setRowPopup(null);
                          openBorrowRepayModal(d.asset, hasBorrow || debtRow ? "repay" : "borrow");
                        }}
                      >
                        Borrow/Repay
                      </button>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* Close Position confirm — same UX as cross trade page */}
      {closePositionModal && (() => {
        const pos = positions.find((p) => (p.pair || `${p.asset}USDT`) === closePositionModal)
          ?? positions.find((p) => closePositionModal.startsWith(p.asset || ""))
          ?? null;
        const quoteAsset = pos?.quote_asset || "USDT";
        const baseAsset = pos?.asset
          || (pos?.pair ? pos.pair.slice(0, -quoteAsset.length) : "")
          || "";
        const pairLabel = pos?.pair
          ? `${pos.pair.slice(0, -quoteAsset.length)}/${quoteAsset}`
          : (baseAsset ? `${baseAsset}/${quoteAsset}` : "—");
        const maxQty = Math.abs(parseFloat(pos?.net_quantity ?? pos?.quantity ?? 0));
        const notional = Math.abs(parseFloat(pos?.value_usdt ?? pos?.notional ?? 0));
        const sideLabel = pos?.side === "LONG" ? "Long" : pos?.side === "SHORT" ? "Short" : "—";
        const qtyDisplay = Number.isFinite(maxQty) && maxQty > 0
          ? maxQty.toFixed(8).replace(/\.?0+$/, "") || "0"
          : "—";

        return (
          <>
            <div className="modal-backdrop fade show" onClick={() => !closePositionLoading && setClosePositionModal(null)} />
            <div
              className="modal fade kyc_modal show d-block"
              tabIndex="-1"
              role="dialog"
              aria-modal="true"
              style={{ zIndex: 1055 }}
            >
              <div className="modal-dialog modal-dialog-centered close-position-modal-dialog" role="document">
                <div className="modal-content close-position-modal-content">
                  <div className="modal-header border-0 pb-1 px-4 pt-4">
                    <h5 className="modal-title fw-bold close-position-modal-title">
                      Close Position
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      disabled={closePositionLoading}
                      onClick={() => setClosePositionModal(null)}
                    />
                  </div>

                  <div className="modal-body px-4 pt-2 pb-0">
                    <div className="d-flex align-items-center gap-2 mb-3 close-position-modal-subtitle">
                      <span className="fw-semibold text-body">{pairLabel}</span>
                      <span>·</span>
                      <span>Cross</span>
                      <span>·</span>
                      <span className={`fw-bold ${pos?.side === "LONG" ? "close-position-modal-side-long" : "close-position-modal-side-short"}`}>
                        {sideLabel}
                      </span>
                    </div>

                    <div className="close-position-settle-card mb-3">
                      <div className="fw-semibold mb-1">Settle the position</div>
                      <div className="close-position-settle-card-desc">
                        Your full holding will be closed as a market order. Remaining assets stay in your Cross Margin account.
                      </div>
                    </div>

                    <div className="mb-3 close-position-info-section">
                      {[
                        { label: "Holding", value: `${qtyDisplay} ${baseAsset}` },
                        { label: "Notional", value: notional ? `${notional.toFixed(4)} ${quoteAsset}` : "—" },
                      ].map(({ label, value }) => (
                        <div key={label} className="d-flex justify-content-between align-items-center py-1 close-position-info-row">
                          <span className="close-position-info-label">{label}</span>
                          <span className="close-position-info-value">{value}</span>
                        </div>
                      ))}
                    </div>

                    <p className="close-position-body-text mb-2">
                      Using Close Position for large sizes may result in losses due to market slippage. Only trading fees are charged.
                    </p>
                    <p className="close-position-body-text-muted mb-3">
                      Amounts received may differ due to the nature of market orders. Larger orders may experience higher slippage. Submit another request if your position is not fully settled.
                    </p>
                  </div>

                  <div className="modal-footer border-0 px-4 pb-4 pt-2">
                    <button
                      type="button"
                      className="btn w-100 close-position-confirm-btn"
                      onClick={handleClosePosition}
                      disabled={closePositionLoading}
                    >
                      {closePositionLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                          Closing…
                        </>
                      ) : "Confirm"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* Modals */}
      <BorrowRepayModal
        key={borrowRepayModal ? `${borrowRepayModal.row?.base}-${borrowRepayModal.tab}` : "empty"}
        row={borrowRepayModal?.row}
        initialTab={borrowRepayModal?.tab || "borrow"}
        lockedMarginType="cross"
        onClose={closeBorrowRepayModal}
        onSuccess={refreshAll}
        onIsolatedClick={() => {
          closeBorrowRepayModal();
          window.location.href = "/user_profile/wallet/margin";
        }}
        onTransferClick={() => {
          const asset = borrowRepayModal?.row?.base;
          closeBorrowRepayModal();
          walletTransfer.openTransferModal({ from: "spot", to: "cross_margin" });
          if (asset) pendingAssetRef.current = asset;
          openTransferBsModal();
        }}
      />

      <WalletTransferModals
        fromWalletType={walletTransfer.fromWalletType}
        setFromWalletType={walletTransfer.setFromWalletType}
        toWalletType={walletTransfer.toWalletType}
        setToWalletType={walletTransfer.setToWalletType}
        availableWalletTypes={walletTransfer.availableWalletTypes}
        walletLabelsMap={walletTransfer.walletLabelsMap}
        selectedCurrency={walletTransfer.selectedCurrency}
        setSelectedCurrency={walletTransfer.setSelectedCurrency}
        transferAmount={walletTransfer.transferAmount}
        setTransferAmount={walletTransfer.setTransferAmount}
        transferable={walletTransfer.transferable}
        balanceLoading={walletTransfer.balanceLoading}
        sourceHint={walletTransfer.sourceHint}
        handleInterchangeWallet={walletTransfer.handleInterchangeWallet}
        handleTransfer={walletTransfer.handleTransfer}
        currencyData={walletTransfer.currencyData}
        fiveDecimal={walletTransfer.fiveDecimal}
        marginInvolved={walletTransfer.marginInvolved}
        marginPairs={walletTransfer.marginPairs}
        selectedMarginPair={walletTransfer.selectedMarginPair}
        setSelectedMarginPair={walletTransfer.setSelectedMarginPair}
        marginAssetType={walletTransfer.marginAssetType}
        setMarginAssetType={walletTransfer.setMarginAssetType}
        marginPairDropOpen={walletTransfer.marginPairDropOpen}
        setMarginPairDropOpen={walletTransfer.setMarginPairDropOpen}
      />
    </>
  );
}
